import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaClock } from "react-icons/fa";
import closeModalIcon from "../../assets/images/closeIconInfo.svg";
import AuditChangesList from "../AuditChangesList";
import { SkeletonModal } from "../SkeletonLoading";
import api from "../../services/api";
import { AuditHistoryRecord } from "../../shared/auditHistory";

type HistoryRecord = AuditHistoryRecord;

interface Props {
  isOpen: boolean;
  title: string;
  historyRoute: string;
  bodyKey: string;
  bodyValue: number;
  onClose: () => void;
}

const ModalHistory: React.FC<Props> = (props: Props) => {
  const [history, setHistory] = useState<HistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post(props.historyRoute, {
        [props.bodyKey]: props.bodyValue,
      });
      setHistory(response.data || []);
    } catch (err: unknown) {
      const errorMsg = err && typeof err === "object" && "response" in err
        ? (err as { response?: { data?: { error_msg?: string } } }).response?.data?.error_msg
        : undefined;
      setError(errorMsg || "Erro ao carregar histórico.");
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [props.historyRoute, props.bodyKey, props.bodyValue]);

  useEffect(() => {
    if (props.isOpen && props.bodyValue) {
      fetchHistory();
    }
  }, [props.isOpen, props.bodyValue, fetchHistory]);

  return (
    <div className="relative">
      <button
        type="button"
        className="fixed inset-0 z-40 bg-black/50 w-full h-full border-none cursor-default"
        onClick={props.onClose}
      />

      <motion.div
        className="fixed top-0 right-0 h-full bg-white shadow-lg z-50 w-full max-w-[700px] rounded-sm flex flex-col"
        initial={{ x: "100%" }}
        animate={{ x: props.isOpen ? 0 : "100%" }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.3 }}
      >
        <div
          className="relative flex justify-start items-center w-full h-10 p-2 pl-3 shrink-0"
          style={{ borderBottom: "1px solid #CCC" }}
        >
          <FaClock className="mr-2 text-[#062D4E]" size={14} />
          <h2 className="text-sm font-medium text-[#1E2121]">
            {props.title}
          </h2>
          <button
            type="button"
            className="absolute right-4 top-3 cursor-pointer bg-transparent border-none p-0"
            onClick={props.onClose}
          >
            <img src={closeModalIcon} alt="Fechar" />
          </button>
        </div>

        <div className="relative flex-1 overflow-y-auto p-5">
          {loading && (
            <div
              className="skeleton-modal-overlay"
              aria-busy="true"
              style={{ "--skeleton-surface": "var(--modal-sidebar-surface)" } as React.CSSProperties}
            >
              <SkeletonModal
                variant="card"
                fields={8}
                showAvatar
                footerButtons={0}
              />
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-32">
              <span className="text-sm text-red-500">{error}</span>
            </div>
          )}

          {!loading && !error && history.length === 0 && (
            <div className="flex items-center justify-center h-32">
              <span className="text-sm text-[#666]">Nenhuma alteração encontrada.</span>
            </div>
          )}

          {!loading && !error && history.length > 0 && (
            <AuditChangesList
              records={history}
              statusContext={`${props.title} ${props.historyRoute}`}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ModalHistory;
